import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { toast } from "sonner";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useQRCodes, type SavedQRCode } from "@/hooks/useQRCodes";
import DownloadModal from "./components/DownloadModal";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const REDIRECT_BASE = "https://getxohype.com/r";

function QRMiniPreview({ qr }: { qr: SavedQRCode }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const redirectUrl = `${REDIRECT_BASE}?code=${qr.shortCode}`;
    QRCode.toCanvas(canvasRef.current, redirectUrl, {
      width: 80,
      margin: 1,
      color: { dark: qr.fgColor, light: qr.bgColor },
      errorCorrectionLevel: qr.errorLevel,
    }).catch(() => {});
  }, [qr]);

  return (
    <div
      className="rounded-lg overflow-hidden flex-shrink-0"
      style={{ backgroundColor: qr.bgColor, width: 80, height: 80 }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
}

function EditModal({
  qr,
  onClose,
  onSave,
}: {
  qr: SavedQRCode;
  onClose: () => void;
  onSave: (id: string, name: string, description: string, url: string) => void;
}) {
  const [name, setName] = useState(qr.name);
  const [description, setDescription] = useState(qr.description || "");
  const [url, setUrl] = useState(qr.targetUrl);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const redirectUrl = `${REDIRECT_BASE}?code=${qr.shortCode}`;
    QRCode.toCanvas(canvasRef.current, redirectUrl, {
      width: 200,
      margin: 2,
      color: { dark: qr.fgColor, light: qr.bgColor },
      errorCorrectionLevel: qr.errorLevel,
    }).catch(() => {});
  }, [qr]);

  const handleSave = () => {
    if (!name.trim() || !url.trim()) return;
    onSave(qr.id, name.trim(), description.trim(), url.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-slate-900">QR Kodu Düzenle</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex gap-6 mb-6">
          <div
            className="rounded-xl flex-shrink-0 flex items-center justify-center"
            style={{ backgroundColor: qr.bgColor, width: 200, height: 200 }}
          >
            <canvas ref={canvasRef} />
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Edge Function Link</p>
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <i className="ri-link-m text-blue-500 text-sm"></i>
              <span className="text-xs font-mono text-slate-700 break-all">{REDIRECT_BASE}?code={qr.shortCode}</span>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Bu QR&apos;ın içindeki link sabit kalır. Sadece hedef URL&apos;yi (yönlendirilecek adres) değiştirirsin.
            </p>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <i className="ri-bar-chart-2-line text-blue-500"></i>
              <span>{qr.scans} tarama</span>
              <span className="text-slate-300">•</span>
              <span>{new Date(qr.createdAt).toLocaleDateString("tr-TR")}</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">QR Kod Adı</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="Örn: Menü QR Kodu"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Açıklama</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="QR kod açıklaması"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Hedef URL
              <span className="ml-2 text-xs text-blue-600 font-normal">— istediğin zaman değiştirebilirsin</span>
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="https://yeni-adres.com"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-full hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || !url.trim()}
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-save-line mr-1.5"></i>
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ qr, onClose, onConfirm }: { qr: SavedQRCode; onClose: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
          <i className="ri-delete-bin-line text-2xl text-red-500"></i>
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2">QR Kodu Sil</h3>
        <p className="text-sm text-slate-500 mb-6">
          <span className="font-medium text-slate-700">&quot;{qr.name}&quot;</span> adlı QR kodunu silmek istediğine emin misin? Bu işlem geri alınamaz.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-full hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            Evet, Sil
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyQRPage() {
  const { qrCodes, update, remove } = useQRCodes();
  const [editingQR, setEditingQR] = useState<SavedQRCode | null>(null);
  const [deletingQR, setDeletingQR] = useState<SavedQRCode | null>(null);
  const [downloadingQR, setDownloadingQR] = useState<SavedQRCode | null>(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const handleSave = async (id: string, name: string, description: string, targetUrl: string) => {
    try {
      await update(id, { name, description, targetUrl });
      setEditingQR(null);
      toast.success("QR kodu güncellendi!");
    } catch (err) {
      toast.error("Güncelleme başarısız: " + (err instanceof Error ? err.message : "Bilinmiyor"));
    }
  };

  const handleDelete = async () => {
    if (!deletingQR) return;
    try {
      await remove(deletingQR.id);
      setDeletingQR(null);
      toast.success("QR kodu silindi.");
    } catch (err) {
      toast.error("Silme başarısız: " + (err instanceof Error ? err.message : "Bilinmiyor"));
    }
  };

  const filtered = qrCodes.filter(
    (qr) =>
      qr.name.toLowerCase().includes(search.toLowerCase()) ||
      (qr.description && qr.description.toLowerCase().includes(search.toLowerCase())) ||
      qr.targetUrl.toLowerCase().includes(search.toLowerCase())
  );

  const typeLabels: Record<string, string> = {
    url: "URL",
    vcard: "vCard",
    wifi: "WiFi",
    text: "Metin",
    email: "E-posta",
    phone: "Telefon",
    instagram: "Instagram",
    whatsapp: "WhatsApp",
    facebook: "Facebook",
    pdf: "PDF",
  };

  const typeIcons: Record<string, string> = {
    url: "ri-global-line",
    vcard: "ri-contacts-line",
    wifi: "ri-wifi-line",
    text: "ri-text",
    email: "ri-mail-line",
    phone: "ri-phone-line",
    instagram: "ri-instagram-line",
    whatsapp: "ri-whatsapp-line",
    facebook: "ri-facebook-line",
    pdf: "ri-file-pdf-line",
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-16">
        <div className="max-w-5xl mx-auto px-6 py-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">QR Kodlarım</h1>
              <p className="text-sm text-slate-500 mt-1">
                Oluşturduğun QR kodları yönet, hedef URL&apos;leri değiştir.
              </p>
            </div>
            <Link
              to="/generator"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Yeni QR Oluştur
            </Link>
          </div>

          {/* Search */}
          {qrCodes.length > 0 && (
            <div className="relative mb-6">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
              <input
                type="text"
                placeholder="QR kod adı, açıklama veya URL ile ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>
          )}

          {/* Empty State */}
          {qrCodes.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-50 rounded-2xl mx-auto mb-4">
                <i className="ri-qr-code-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Henüz QR kodun yok</h3>
              <p className="text-sm text-slate-500 mb-6">
                İlk dinamik QR kodunu oluştur ve hedef URL&apos;yi istediğin zaman değiştir.
              </p>
              <Link
                to="/generator"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                İlk QR Kodunu Oluştur
              </Link>
            </div>
          )}

          {/* QR List */}
          {filtered.length > 0 && (
            <div className="space-y-3">
              {filtered.map((qr) => (
                <div
                  key={qr.id}
                  className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4 hover:border-blue-200 transition-colors"
                >
                  <QRMiniPreview qr={qr} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-slate-900 truncate">{qr.name}</span>
                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                        <i className={`${typeIcons[qr.type] || "ri-qr-code-line"} text-xs`}></i>
                        {typeLabels[qr.type] || qr.type}
                      </span>
                    </div>

                    {qr.description && (
                      <p className="text-xs text-slate-500 mb-1 truncate">{qr.description}</p>
                    )}

                    <div className="flex items-center gap-1.5 mb-1">
                      <i className="ri-link-m text-blue-500 text-xs flex-shrink-0"></i>
                      <span className="text-xs font-mono text-slate-500 truncate max-w-[120px]">{REDIRECT_BASE}?code={qr.shortCode}</span>
                      <i className="ri-arrow-right-line text-slate-300 text-xs"></i>
                      <span className="text-xs text-slate-600 truncate">{qr.targetUrl}</span>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-bar-chart-2-line text-blue-400"></i>
                        {qr.scans} tarama
                      </span>
                      <span className="text-slate-200">•</span>
                      <span>{new Date(qr.createdAt).toLocaleDateString("tr-TR")}</span>
                      <span className="text-slate-200">•</span>
                      <button
                        onClick={() => navigate(`/statistics`)}
                        className="text-blue-500 hover:text-blue-600 font-medium cursor-pointer"
                      >
                        İstatistikleri Gör
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setDownloadingQR(qr)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                      title="İndir"
                    >
                      <i className="ri-download-2-line text-base"></i>
                    </button>
                    <button
                      onClick={() => setEditingQR(qr)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                      title="Düzenle"
                    >
                      <i className="ri-edit-line text-base"></i>
                    </button>
                    <button
                      onClick={() => setDeletingQR(qr)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      title="Sil"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No search results */}
          {qrCodes.length > 0 && filtered.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
              <p className="text-sm text-slate-500">
                &quot;<span className="font-medium text-slate-700">{search}</span>&quot; için sonuç bulunamadı.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {editingQR && (
        <EditModal
          qr={editingQR}
          onClose={() => setEditingQR(null)}
          onSave={handleSave}
        />
      )}
      {deletingQR && (
        <DeleteConfirmModal
          qr={deletingQR}
          onClose={() => setDeletingQR(null)}
          onConfirm={handleDelete}
        />
      )}
      {downloadingQR && (
        <DownloadModal
          qr={downloadingQR}
          onClose={() => setDownloadingQR(null)}
        />
      )}

      <Footer />
    </div>
  );
}